import React, { useRef } from "react";
import { ChatMessageAttachmentType } from "@jugl-web/rest-api";
import { cx, normalizeAudioAmps, useTranslations } from "@jugl-web/utils";
import { InteractiveContainer, Menu, MenuItem } from "@jugl-web/ui-components";
import { VoiceMessageAudioPlayer } from "./components/VoiceMessageAudioPlayer";
import { ChatAudioPlayer } from "./components/ChatAudioPlayer";
import { useChatMessageBubble } from "../../providers/ChatMessageBubbleProvider/useChatMessageBubble";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ChatMessageBubbleAction } from "../../types/ChatMessageBubbleAction";

export const AudioAttachment: React.FC = () => {
  const {
    attachments,
    isOutgoing,
    getAttachmentUrls,
    isPending,
    onDeleteAttachment,
    availableActions,
    isMessageActionsDisabled,
  } = useChatMessageBubble();
  const { t } = useTranslations();
  const triggeredByContextMenu = useRef(false);

  return (
    <div className="flex flex-col gap-1">
      {attachments?.map((attachment, index) => {
        if (
          ![
            ChatMessageAttachmentType.audio,
            ChatMessageAttachmentType.voice,
          ].includes(attachment.type)
        ) {
          return null;
        }

        const { stream } = getAttachmentUrls(attachment);

        if (attachment.type === ChatMessageAttachmentType.voice) {
          return (
            <VoiceMessageAudioPlayer
              key={+index}
              audioSrc={stream}
              waveform={normalizeAudioAmps(attachment.amps || [0])}
              duration={attachment.duration}
              variant={isOutgoing ? "outgoing" : "incoming"}
            />
          );
        }
        const $audioPlayer = (
          <ChatAudioPlayer
            key={+index}
            src={stream}
            name={
              attachment.name ||
              t({
                id: "chats-page.audio-file",
                defaultMessage: "Audio file",
              })
            }
            variant={isOutgoing ? "outgoing" : "incoming"}
            duration={attachment.duration}
          />
        );

        const menuItems: MenuItem[] =
          onDeleteAttachment &&
          attachments.length > 1 &&
          availableActions?.includes(ChatMessageBubbleAction.EDIT) &&
          !isMessageActionsDisabled
            ? [
                {
                  id: "delete",
                  label: t({
                    id: "chats-page.delete-file",
                    defaultMessage: "Delete file",
                  }),
                  icon: <DeleteIcon />,
                  onSelect: () => onDeleteAttachment(attachment.uid),
                },
              ]
            : [];

        if (!menuItems.length) {
          return $audioPlayer;
        }

        return (
          <Menu
            key={+index}
            hasBackdrop
            placement="bottom-end"
            autoClose
            sections={[menuItems]}
            renderTrigger={({ Trigger, triggerRef, isOpen }) => (
              <Trigger
                as={InteractiveContainer}
                isDisabled={isPending}
                className={cx(
                  "flex w-full items-start gap-2 rounded-lg transition-colors",
                  isOutgoing ? "hover:bg-[#3E88C3]" : "hover:bg-grey-100",
                  isOpen && isOutgoing && "bg-[#3E88C3]",
                  isOpen && !isOutgoing && "bg-grey-100",
                  isPending && "pointer-events-none"
                )}
                ref={triggerRef}
                onPointerDown={(e) => {
                  if (triggeredByContextMenu.current) {
                    triggeredByContextMenu.current = false;
                    return;
                  }

                  // Headless UI v2 opens menus on pointerdown. Block that for regular clicks (LMB)
                  if (e.button === 0) {
                    e.preventDefault();
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  triggeredByContextMenu.current = true;
                  e.currentTarget.dispatchEvent(
                    new PointerEvent("pointerdown", {
                      bubbles: true,
                      cancelable: true,
                      button: 0,
                      pointerType: "mouse",
                    })
                  );
                }}
                onClick={(e) => {
                  if (triggeredByContextMenu.current) {
                    triggeredByContextMenu.current = false;
                    return;
                  }
                  e.preventDefault();
                }}
              >
                {$audioPlayer}
              </Trigger>
            )}
          />
        );
      })}
    </div>
  );
};
